// Top-level signup wizard component. Loads or restores a draft on
// mount, fetches the live wizard config, and renders the current
// stage via the single CompoundStep renderer.
//
// MR-A: every stage — regardless of kind — renders through CompoundStep,
// which walks the stage's blocks[] and resolves them against the
// bundled libraryEntries[]. The per-kind switch is gone.

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  createDraft,
  fetchSignupConfig,
  fetchWizardConfig,
  getDraft,
  patchDraft,
  loadStoredCredentials,
  clearCredentials,
  type DraftPayload,
  type DraftView,
  type WizardConfig,
} from './lib/draftClient';
import { firstStage, nextStage, previousStage } from './lib/branching';
import { Shell, selectTestimonials, type TestimonialDisplay } from './components/Shell';
import { TurnstileWidget } from './components/TurnstileWidget';
import { CompoundStep } from './steps/CompoundStep';

export interface StepProps {
  draft: DraftView;
  config: WizardConfig;
  setDraft: (next: DraftView) => void;
  advance: (patch?: Partial<DraftPayload>) => Promise<void>;
  back: () => void;
  isAdvancing: boolean;
}

interface BootState {
  draft: DraftView | null;
  config: WizardConfig | null;
  /**
   * Turnstile site key from /api/public/signup/config, resolved during
   * the 'loading' phase. Null means the challenge is disabled. Only read
   * once phase !== 'loading', so the widget never mounts against a
   * not-yet-fetched value.
   */
  turnstileSitekey: string | null;
  loading: boolean;
  error: string | null;
}

// Three-phase boot:
//   1. 'loading' — fetching wizard config + runtime client config
//                  (Turnstile site key) + checking sessionStorage
//   2. 'ready'   — render the active stage
//
// There is deliberately NO challenge phase. Turnstile used to gate the
// whole wizard: the visitor stared at a bot check before seeing a single
// field, and the draft was created the moment it resolved. That is
// backwards for a signup funnel — the challenge is dead time in front of
// the form, and every second of it is lead loss.
//
// Now the first stage renders immediately, the widget mounts alongside it
// and resolves in the background while the visitor types, and the token is
// only REQUIRED at the first advance — by which point it has almost always
// already arrived, so the check costs nothing. The bot protection is
// unchanged: no draft is ever created without a verified token, because
// draft creation itself is what moved.
type BootPhase = 'loading' | 'ready';

/**
 * Stand-in draft for the pre-submit state.
 *
 * Stage 1 renders before any draft exists — the draft is minted at the
 * first advance — but CompoundStep reads `draft.payload` to prefill
 * fields and evaluate block visibility. An empty payload is exactly
 * right there: nothing has been answered yet. Form values themselves
 * live in the stage's form registry (uncontrolled inputs), not in the
 * draft, so nothing is lost by not having a real one.
 *
 * ONLY the first stage can ever see this. Every later stage is reached
 * through advance(), which mints the real draft before navigating — so
 * the draft-scoped blocks (Stripe setup-intent, OTP verification,
 * complete) never run against it. `draftId` is a sentinel rather than
 * an empty string so that if that invariant is ever broken, the failing
 * request is obvious in a server log instead of looking like a
 * malformed id.
 */
const PENDING_DRAFT: DraftView = {
  draftId: '__pending__',
  currentStep: null,
  emailVerified: false,
  smsVerified: false,
  completedCustomerId: null,
  expiresAt: '',
  payload: {},
};

export function App() {
  const [boot, setBoot] = useState<BootState>({
    draft: null,
    config: null,
    turnstileSitekey: null,
    loading: true,
    error: null,
  });
  const [phase, setPhase] = useState<BootPhase>('loading');
  const [stageId, setStageId] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);

  // ── Boot ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Parallel: the runtime client config is an independent fetch,
        // so resolving it alongside the wizard config costs no extra
        // boot latency.
        const [config, clientConfig] = await Promise.all([
          fetchWizardConfig(),
          fetchSignupConfig(),
        ]);
        const turnstileSitekey = clientConfig.turnstileSitekey;
        const stored = loadStoredCredentials();
        let draft: DraftView | null = null;
        if (stored) {
          try {
            draft = await getDraft(stored.draftId, stored.token);
            if (draft.completedCustomerId) {
              clearCredentials();
              draft = null;
            }
          } catch {
            clearCredentials();
            draft = null;
          }
        }
        if (cancelled) return;
        // Render the wizard either way. With a restored draft we resume
        // where the visitor left off; without one we start at the first
        // stage and mint the draft on their first advance.
        setBoot({ draft, config, turnstileSitekey, loading: false, error: null });
        setStageId(draft?.currentStep ?? firstStage(config)?.id ?? null);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setBoot({
          draft: null,
          config: null,
          turnstileSitekey: null,
          loading: false,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Turnstile token plumbing ─────────────────────────────────────────
  // The token lands asynchronously while the visitor fills in stage 1, so
  // it is held in a ref (no re-render needed) and any advance that arrives
  // before it resolves parks on a waiter rather than failing.
  const turnstileTokenRef = useRef<string | null>(null);
  const turnstileWaitersRef = useRef<((token: string) => void)[]>([]);

  const onTurnstileVerified = useCallback((token: string) => {
    turnstileTokenRef.current = token;
    // Release anyone already waiting at the submit gate.
    const waiters = turnstileWaitersRef.current.splice(0);
    waiters.forEach((resolve) => resolve(token));
  }, []);

  /**
   * Resolves the Turnstile token for draft creation.
   *
   * Returns '' when the challenge is disabled (no site key) — the API
   * mirrors that no-op. Returns null if the token never arrives within
   * the timeout, which the caller surfaces as a retryable error rather
   * than silently POSTing a null token the API would reject anyway.
   *
   * In practice this resolves instantly: the widget has been working
   * since page load, so by the time anyone finishes stage 1 the token is
   * already in the ref and this never awaits at all.
   */
  const awaitTurnstileToken = useCallback(
    (timeoutMs = 20000): Promise<string | null> => {
      if (turnstileTokenRef.current !== null) {
        return Promise.resolve(turnstileTokenRef.current);
      }
      return new Promise((resolve) => {
        const timer = window.setTimeout(() => {
          turnstileWaitersRef.current = turnstileWaitersRef.current.filter((w) => w !== waiter);
          resolve(null);
        }, timeoutMs);
        const waiter = (token: string) => {
          window.clearTimeout(timer);
          resolve(token);
        };
        turnstileWaitersRef.current.push(waiter);
      });
    },
    [],
  );

  // ── Stage navigation ─────────────────────────────────────────────────
  const advance = useCallback(
    async (patch?: Partial<DraftPayload>) => {
      if (!boot.config || !stageId) return;
      setAdvancing(true);
      try {
        // First advance on a fresh visit: this is the submit gate. Mint the
        // draft now, with the token the widget has been earning in the
        // background. Creating it here rather than at page load is what lets
        // stage 1 render instantly — and it means an abandoned visit that
        // never submits leaves no draft row behind, so draft counts now
        // measure intent rather than pageviews.
        let draft = boot.draft;
        if (!draft) {
          const token = await awaitTurnstileToken();
          if (token === null) {
            setTurnstileError('turnstile-timeout');
            return;
          }
          const created = await createDraft({
            payload: capturedUtm(),
            turnstileToken: token || null,
          });
          draft = await getDraft(created.draftId, created.token);
          setBoot((b) => ({ ...b, draft }));
        }

        const stored = loadStoredCredentials();
        if (!stored) {
          setBoot((b) => ({ ...b, error: 'Session lost. Please reload to start over.' }));
          return;
        }
        const nextPayload: DraftPayload = { ...draft.payload, ...(patch ?? {}) };
        const next = nextStage(boot.config, stageId, nextPayload);
        const updated = await patchDraft(draft.draftId, stored.token, {
          payload: patch ?? {},
          currentStep: next?.id ?? `__end:${stageId}`,
        });
        setBoot((b) => ({ ...b, draft: updated }));
        if (next) {
          setStageId(next.id);
        } else {
          // No further stages — the last stage was the terminal one
          // (typically a Review or Completed block). The block itself
          // owns the completion side-effect; we just stop navigating.
          setCompletedAt(null);
        }
      } catch (e) {
        setBoot((b) => ({ ...b, error: e instanceof Error ? e.message : String(e) }));
      } finally {
        setAdvancing(false);
      }
    },
    [boot.draft, boot.config, stageId, awaitTurnstileToken],
  );

  const back = useCallback(() => {
    if (!boot.config) return;
    if (!stageId) return;
    const prev = previousStage(boot.config, stageId);
    if (prev) setStageId(prev.id);
  }, [boot.config, stageId]);

  // ── Render ───────────────────────────────────────────────────────────
  if (phase === 'loading' || boot.loading) {
    return (
      <Shell>
        <div className="text-center text-sm text-[var(--bcbb-text-muted)]">Loading…</div>
      </Shell>
    );
  }
  if (boot.error || !boot.config) {
    return (
      <Shell>
        <div className="rounded-md bg-[var(--bcbb-danger-bg,#fde0df)] px-4 py-3 text-sm text-[var(--bcbb-danger)]">
          {boot.error ?? 'Something went wrong starting the signup wizard. Please reload.'}
        </div>
      </Shell>
    );
  }
  if (completedAt) {
    return (
      <Shell>
        <div className="flex flex-col gap-4 text-center">
          <h3 className="m-0 text-2xl font-semibold">You're in! 🎉</h3>
          <p className="m-0 text-sm text-[var(--bcbb-text-muted)]">
            Redirecting to your portal…
          </p>
        </div>
      </Shell>
    );
  }

  const stage = boot.config.stages.find((s) => s.id === stageId);
  if (!stage) {
    return (
      <Shell>
        <div className="text-sm text-[var(--bcbb-text-muted)]">
          The signup wizard isn't fully configured yet. Please check back soon.
        </div>
      </Shell>
    );
  }

  const display = stage.display;
  const pool: TestimonialDisplay[] = boot.config.testimonials ?? [];
  const testimonials = selectTestimonials(pool, display?.testimonials);
  const header = display?.header ?? stage.title;

  return (
    <Shell
      header={header}
      subheader={display?.subheader}
      body={display?.body}
      testimonials={testimonials}
    >
      <CompoundStep
        stage={stage}
        draft={boot.draft ?? PENDING_DRAFT}
        config={boot.config}
        setDraft={(d) => setBoot((b) => ({ ...b, draft: d }))}
        advance={advance}
        back={back}
        isAdvancing={advancing}
      />
      {/*
        Mounted for the whole wizard, not as a gate in front of it. While
        the draft is unminted the widget is quietly earning the token that
        the first advance will consume; Cloudflare's managed mode is
        usually invisible, and when it does need interaction it sits below
        the form rather than replacing it.

        Kept mounted after the draft exists too: remounting on every stage
        would re-run the challenge for no reason, and the token is only
        ever consumed once.
      */}
      {!boot.draft && (
        <div className="mt-4">
          <TurnstileWidget
            sitekey={boot.turnstileSitekey}
            onVerified={onTurnstileVerified}
            onError={(reason) => setTurnstileError(reason)}
          />
          {turnstileError && (
            <div className="mt-2 rounded-md bg-[var(--bcbb-danger-bg,#fde0df)] px-3 py-2 text-sm text-[var(--bcbb-danger)]">
              {turnstileError === 'turnstile-timeout'
                ? "We couldn't finish the bot check in time. Please reload and try again."
                : `Verification failed (${turnstileError}). Please reload the page to try again.`}
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}

function capturedUtm(): DraftPayload {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const k of ['source', 'medium', 'campaign', 'term', 'content']) {
      const v = params.get(`utm_${k}`);
      if (v) utm[k] = v;
    }
    return Object.keys(utm).length > 0 ? { utm } : {};
  } catch {
    return {};
  }
}
